import type { RangeType as DateRangeType } from 'rsuite/lib/DatePicker';
import type { RangeType as DateRangeRangeType } from 'rsuite/lib/DateRangePicker';
import type { BarcodeFormat } from "@zxing/library";

import type { ImageFormat } from "@Utils/Dom";
import type { TimeFormat } from "@Utils/Formats";
import type { ItemsStyle } from "@Utils/Colors";
import type { ButtonLabelPosition, ButtonValueType } from './Button';
import type { InfoTipItem } from './InfoTips';
import type { BarcodeScannerCodeFormat } from "../Widgets/Things/Image/BarcodeScanner";
import type { IAdvancedTippy } from '@Primitive/AdvancedTippy';
import { PresetColor } from 'react-color/lib/components/sketch/Sketch';
// --------------------------------------------------
export enum SettingsType {
    Number = 0,
    NumberStr = "number",
    Boolean = 1,
    BooleanStr = "boolean",
    ColorPicker = 2,
    ColorPickerStr = "color",
    String = 3,
    StringStr = "string",
    DropDown = 4,
    DropDownStr = "dropdown",
    Dialog = 5,
    DialogStr = "dialog",
    ImageFileInput = 6,
    ImageFileInputStr = "image-file",
    Slider = 7,
    SliderStr = "slider",
    ItemsMgmtDialog = 8,
    ItemsMgmtDialogStr = "items-dialog",
    Icon = 9,
    IconStr = "icon",
    Generic = 10,
    GenericStr = "generic",
    ListItem = 11,
    ListItemStr = "list-item",
    Group = 12,
    GroupStr = "group",
    ExtendedIcon = 13,
    ExtendedIconStr = "extended-icon",
    MarkdownString = 14,
    MarkdownStringStr = "markdown",
    PaletteColorPicker = 15,
    PaletteColorPickerStr = "palette-color-picker",
    Highlights = 16,
    HighlightsStr = "highlights",
    Datetime = 17,
    DatetimeStr = "datetime",
    Time = 18,
    TimeStr = "time",
    Barcode = 19,
    BarcodeStr = "barcode",
    Button = 20,
    ButtonStr = "button",
    Image = 21,
    ImageStr = "image",
    DatetimeRange = 22,
    DatetimeRangeStr = "datetime-range",
    InfoTip = 23,
    InfoTipStr = 'infotip',
    TextEditor = 24,
    TextEditorStringStr = "texteditor",
    ListOfItems = 25,
    ListOfItemsStr = 'list-of-items',
    CodeEditor = 26,
    CodeEditorStr = 'code-editor',
    Attachments = 27,
    AttachmentsStr = 'attachments',
    RadioButtons = 28,
    RadioButtonsStr = 'radio-buttons',
    ButtonGroup = 29,
    ButtonGroupStr = 'button-group',
    TabsGroup = 30,
    TabsGroupStr = 'tabs-group'
}
// --------------------------------------------------
export enum eIconType {
    fontAwesome = 0,
    url = 1,
    hosted = 2
}
// --------------------------------------------------
export interface GaugeHelperHighlight extends CanvasGauges.Highlight {
    textColor?: string;
}
// --------------------------------------------------
export declare type GaugeHelperHighlightDictionary = { [key: string]: GaugeHelperHighlight[] };
// --------------------------------------------------
export interface DropDownItem {
    value: string | number | boolean;
    label: string;
    options?: {
        value: string | number | boolean;
        label: string;
    }[];
}
//-----------------------------------------------------
export interface ListItem {
    /** Set title/subtitle data accessors */
    id: string | number;
    titleAccessor: string;
    subtitleAccessor?: string;
    /** Be able to set color of title/subtitle from data */
    titleColorAccessor?: string;
    subtitleColorAccessor?: string;
    /** Define the icon/image from data */
    leftItemType?: 'image' | 'icon';
    leftItemAccessor?: string;
    leftItemDefault?: string;
}
// ------------------------------------------------------------------------------------------------
export interface SettingsElement<T> {
    type: SettingsType;
    label: string;
    leftIcon?: React.ReactNode;

    /**
     * DataPath in the form of xxx.yyy.zzz. empty string is mean the root
     */
    dataPath: string;
    isHidden?: (data: T) => boolean;

    /**
     * The element can be nullable.
     * UI adding extra field to make empty value
     * Default: false
     */
    isNullable?: boolean;

    /**
     * Tooltip support
     */
    tooltip?: string;

    /**
     * Readonly
     */
    readonly?: boolean;

    /**
     * Disable edit mode
     */
    disable?: boolean;

    /**
     * Layout width steps
     * Default: 12
     */
    gridStepsNumber?: number;

    /**
     * Set the position of the element to the end of the row
     */
    gridToRowEnd?: boolean;

    /**
     * Style the element
     */
    style?: ItemsStyle;

    /**
     * Define if this field is mandatory.
     * Default: False
     */
    mandatory?: boolean;

    /**
     * Define a validation pattern
     */
    validationPattern?: string;

    /**
     * Wrong validation error message
     */
    validationErrorMsg?: string;
}
export interface SettingsElementGeneric<T> extends SettingsElement<T> {
    type: SettingsType.Generic | SettingsType.GenericStr;
    typeFunc: (data: T) => SettingsType;
}
export interface SettingsElementNumber<T> extends SettingsElement<T> {
    type: SettingsType.Number | SettingsType.NumberStr;
    isDecimal?: boolean;
    minValue?: number;
    maxValue?: number;
    step?: number;
    oneLine?: boolean;
    widthInput?: number;
    autoFocus?: boolean;

    postfix?: string;
    postfixIcon?: string;
    postfixIsButton?: boolean;

    prefix?: string;
    prefixIcon?: string;
    prefixIsButton?: boolean;
}
export interface SettingsElementBoolean<T> extends SettingsElement<T> {
    type: SettingsType.Boolean | SettingsType.BooleanStr;

    oneLine?: boolean;

    // In the case of item mgmt allow only one checked
    onlyOneChecked?: boolean;

    // UI layout, default: toggle
    layout?: 'toggle' | 'checkbox';
}
export interface SettingsElementColorPicker<T> extends SettingsElement<T> {
    type: SettingsType.ColorPicker | SettingsType.ColorPickerStr;
    oneLine?: boolean;
    disableAlpha?: boolean;
    presetColors?: PresetColor[];
}
export interface SettingsElementString<T> extends SettingsElement<T> {
    type: SettingsType.String | SettingsType.StringStr;
    fieldType?: string;
    oneLine?: boolean;
    isTextArea?: boolean;
    widthInput?: number;
    autoFocus?: boolean;
    placeholder?: string;

    postfix?: string;
    postfixIcon?: string;
    postfixIsButton?: boolean;

    prefix?: string;
    prefixIcon?: string;
    prefixIsButton?: boolean;

    onEnterPressButtonDataPath?: string;
}

export interface SettingsElementDatetime<T> extends SettingsElement<T> {
    type: SettingsType.Datetime | SettingsType.DatetimeStr;
    format: string;
    oneLine?: boolean;
    widthInput?: number;
    placement?: any;
    ranges?: DateRangeType[];
    defaultValue?: Date;
    disabledWeekends?: boolean;
    isoWeek?: boolean;
}

export interface SettingsElementDatetimeRange<T> extends SettingsElement<T> {
    type: SettingsType.DatetimeRange | SettingsType.DatetimeRangeStr;
    format: string;
    oneLine?: boolean;
    widthInput?: number;
    placement?: any;
    ranges?: DateRangeRangeType[];
    disabledWeekends?: boolean;
    withNavigation?: boolean;
    leftArrowTooltip?: string;
    rightArrowTooltip?: string;
    /**
     * (useful for mobile apps).
     */
    isMobileView?: boolean;
    isoWeek?: boolean;
}

export interface SettingsElementTime<T> extends SettingsElement<T> {
    type: SettingsType.Time | SettingsType.TimeStr;
    format: TimeFormat;
    oneLine?: boolean;
    widthInput?: number;
}
export interface SettingsElementIcon<T> extends SettingsElement<T> {
    type: SettingsType.Icon | SettingsType.IconStr;
    oneLine?: boolean;
}
export interface SettingsElementExtendedIcon<T> extends SettingsElement<T> {
    type: SettingsType.ExtendedIcon | SettingsType.ExtendedIconStr;
    iconTypePath: string;
    supportUrl?: boolean;
    supportUpload?: ExtendedIconUploadConf;
}

export interface SettingsElementBarcodeScanner<T> extends SettingsElement<T> {
    type: SettingsType.Barcode | SettingsType.BarcodeStr;
    autoStart: boolean;
    autoSend: boolean;
    autoClose: boolean;
    autoFocus: boolean;
    codeFormat: BarcodeScannerCodeFormat;
    hints: BarcodeFormat[];
    oneLine?: boolean;
    widthInput?: number;

    scanditEnable?: boolean;
    scanditLicense?: string;
    scanditInitZoom?: number;
}

export interface ExtendedIconUploadConf {
    /**
     * Default: 24
     */
    maxWidth?: number;

    /**
     * Default: 24
     */
    maxHeight?: number;

    /**
     * Default "png"
     */
    ext?: ImageFormat;

    /**
     * Ranges in (0,1]
     * Default: 0.8
     */
    imgQuality?: number;
}

export interface SettingsElementDropDown<T> extends SettingsElement<T> {
    type: SettingsType.DropDown | SettingsType.DropDownStr;
    items: DropDownItem[];

    /**
     * Displays label and input in separate rows (useful for long labels).
     */
    inSeperateLine?: boolean;

    /**
     * Define the width of the input item
     */
    widthInput?: number;

    /**
     * Support multiple selected options.
     */
    isMulti?: boolean;

    /**
     * Allow the user to create new options if they don't already exist.
     */
    isCreatable?: boolean;

    /**
     * Clears any selected option(s).
     */
    isClearable?: boolean;

    /**
     * It limits the number of selections.
     */
    maxNumOfSelections?: number;

    /**
     * Opens the drop-down list in a dialog (useful for mobile apps).
     */
    useDialog?: boolean;
    isSearchableWhenUsingDialog?: boolean;

    /**
     * Hide the selected option from the menu.
     */
    hideSelectedOptions?: boolean;

    /**
     * Hide SelectAll in multiple selection
     * Default: false
     */
    hideSelectAll?: boolean;

    /**
     * Close drop down menu on select
     * Default: true
     */
    closeMenuOnSelect?: boolean;

    /**
     * Support grouped items
     * Default: false
     */
    isGroupedItems?: boolean;
}
export interface SettingsDialogInfo<T, K> {
    rootData: any;
    itemIndex?: number; // This is for the case of item mgmt dialog

    data: T;
    privData: K;
    closeDialogCB: () => void;
    updateData: (data: T) => void;
    updatePrivData: (data: K) => void;
}
export type SettingsDialogTitleGetter = (data: any) => React.ReactNode
export interface SettingsDialogConf<T, K> {
    /**
     * Title
     */
    title?: string | React.ReactNode | SettingsDialogTitleGetter;

    /**
     * Static size for the body based on percentage [0-1]
     */
    contentStaticSize?: number | { w: number, h: number };

    /**
     * Init a priv data for dialog system
     */
    initPrivData?: (data: T) => K;

    /**
     * Dynamic react node body.
     * The function will be call every time the dialog is refresh or resized.
     *
     * The width and height is valid only when we are using static content size
     */
    bodyFunc: (width: number, height: number, dialogInfo: SettingsDialogInfo<T, K>) => React.ReactNode;

    /**
     * Array of buttons
     */
    actionsFunc?: (dialogInfo: SettingsDialogInfo<T, K>) => React.ReactElement<any>[];
}
export interface SettingsElementDialog<T> extends SettingsElement<T> {
    type: SettingsType.Dialog | SettingsType.DialogStr;
    icon: React.ReactNode;
    dialogConf: SettingsDialogConf<any, any>;
}
export interface SettingsElementImageFileInput<T> extends SettingsElement<T> {
    type: SettingsType.ImageFileInput | SettingsType.ImageFileInputStr;
    previewImg: boolean;
    externalURL: boolean;
    acceptButton?: boolean;

    fileInterceptor?: (value: string) => Promise<string>;
    acceptButtonFunc?: (value: string, data: T) => void;

    // Image Settings
    imgSettings?: {
        maxWidth: number;
        maxHeight: number;
        format: 'png' | 'gif' | 'bmp' | 'jpeg' | 'webp';
        quality: number;
    }
}

// attachments
// --------------------------------------------------------------------------------
export enum eAttachmentAction {
    upload = 'upload-attachment',
    edit = 'edit-attachment',
    delete = 'delete-attachment'
}

export interface IAttachmentAction {
    action: eAttachmentAction;
    dataPath: string;
    attachment: IAttachment;
}

export interface IAttachment {
    id: string | number;
    name: string;
    src: string;
    isImage: boolean;
    info: any;
}

export interface SettingsElementAttachments<T> extends SettingsElement<T> {
    type: SettingsType.Attachments | SettingsType.AttachmentsStr;
    fileInputLabel: string;
    fileInputStyle: ItemsStyle;

    // all of them, default: true
    uploadable: boolean;
    downloadable: boolean;
    editable: boolean;
    deletable: boolean;
}
// --------------------------------------------------------------------------------

export interface SettingsElementRadioButtons<T> extends SettingsElement<T> {
    type: SettingsType.RadioButtons | SettingsType.RadioButtonsStr;
    radioInline: boolean;
    appearance: 'default' | 'picker';
    items: DropDownItem[];
    oneLine: boolean;
    widthInput: number;
}

export interface SettingsElementSlider<T> extends SettingsElement<T> {
    type: SettingsType.Slider | SettingsType.SliderStr;
    minValue: number;
    maxValue: number;
    stepValue: number;
    valuePosfix: string;
    hideValue?: boolean;
    color?: string;
    icon?: string;
    rightIcon?: string;
    oneLine?: boolean;
    widthInput?: number;
}
export interface SettingsElementListItem<T> extends SettingsElement<T> {
    type: SettingsType.ListItem | SettingsType.ListItemStr;
    secondaryText?: string;
    leftIcon?: any;
    rightIconButtonsFunc?: (data: T, updateData: (newData: T) => void) => React.ReactElement<any>;
}
export interface SettingsElementGroup<T> extends Partial<SettingsElement<T>>, SettingsGroup<T> {
    type: SettingsType.Group | SettingsType.GroupStr;
}
export interface SettingsElementCode<T> extends SettingsElement<T> {
    type: SettingsType.CodeEditor | SettingsType.CodeEditorStr;
    icon: React.ReactNode | string;
    codeType: 'handlebars' | 'javascript' | 'sql' | 'html' | 'json';
}
export interface SettingsElementMarkdownString<T> extends SettingsElement<T> {
    type: SettingsType.MarkdownString | SettingsType.MarkdownStringStr;
    icon: React.ReactNode | string;
}
export interface SettingsElementTextEditorString<T> extends SettingsElement<T> {
    type: SettingsType.TextEditor | SettingsType.TextEditorStringStr;
    icon: React.ReactNode;
}
export interface SettingsElementPalette<T> extends SettingsElement<T> {
    type: SettingsType.PaletteColorPicker | SettingsType.PaletteColorPickerStr;
}
export interface SettingsElementHighlights<T> extends SettingsElement<T> {
    type: SettingsType.Highlights | SettingsType.HighlightsStr;
    icon?: React.ReactNode;
    showTextColor?: boolean;
    enableDecimal?: string | boolean;
    minValue?: string | number;
    maxValue?: string | number;
    /**
     * Support dictionary of highlights.
     * When enabled, the element expects {[key:string]: Highlights[]}
     * When disabled, the element expects Highlights[]
     */
    isMulti?: boolean;
}
export interface SettingsElementButton<T> extends SettingsElement<T> {
    type: SettingsType.Button | SettingsType.ButtonStr;

    /**
     * Secondary label when the layout is 'list-item'
     */
    secondaryLabel?: string;

    /**
     * Define the layout of the button
     * Default normal
     */
    layout?: 'normal' | 'list-item';

    /** Don't check for mandatory before sending */
    forceSend?: boolean;

    oneLine?: boolean;

    /**
     * Theme
     */
    labelPosition?: ButtonLabelPosition;
    borderRadius?: number | string;
    backgroundColor?: string;
    fontColor?: string;
    fontSize?: number;
    icon?: string;
    iconSize?: number;
    iconWidth?: number;
    iconType?: ButtonValueType;
    uppercase?: boolean;
    shadowLevel?: number;
    height?: number;
    width?: number;
    margin?: string;
    textAlign?: "center" | "left" | "right";

    advancedTooltip?: IAdvancedTippy;
    advancedPopup?: IAdvancedTippy;
    splitButton?: boolean;

    // these paths should be filled to enable button
    mandatoryDataPaths?: string[];
}

export interface SettingsElementButtonGroup<T> extends SettingsElement<T> {
    type: SettingsType.ButtonGroup | SettingsType.ButtonGroupStr;
    buttons: SettingsElementButton<T>[];
    justifyContent: 'flex-start' | 'center' | 'flex-end' | 'space-between' | 'space-around' | 'space-evenly';
}

export interface SettingsElementListOfItems<T> extends SettingsElement<T> {
    type: SettingsType.ListOfItems | SettingsType.ListOfItemsStr;
    height?: number | string;
    listItemFormFactor?: "sm" | "md" | "lg";
    isHoverable?: boolean;
    isSelectable?: boolean;
    isSearchable?: boolean;

    /** Set title/subtitle data accessors */
    idAccessor: string;
    titleAccessor: string;
    subtitleAccessor?: string;
    leftIconAccessor?: string;
    tooltipAccessor?: string;

    /** Be able to set color of title/suNbtitle from data */
    titleColorAccessor?: string;
    subtitleColorAccessor?: string;
    selectedAccessor?: string;
    isNonSelectableAccessor?: string;


    /** Define the icon/image from data */
    leftIconDefault?: string;
    leftIconTypeAccessor?: 'image' | 'font';

    /**
     * Define accessor from where can be specified if right buttons will be show/hide.
     */
    showRightButtonAccessor?: string;

    /** Right button icon  */
    rightButtons?: {
        key: string,
        type: ButtonValueType;
        label?: string;
        tooltip?: string;

        // Hide button based on the data
        isHiddenAccessor?: string;
        isHiddenDefault?: boolean;

        /**
         * Theme
         */
        borderRadius?: number;
        backgroundColor?: string;
        fontColor?: string;
        fontSize?: number;
        icon?: string;
        iconSize?: number;
        uppercase?: boolean;
        shadowLevel?: number;
        height?: number;
        width?: number;
        margin?: string;
    }[];
}
export interface SettingsElementImage<T> extends SettingsElement<T> {
    type: SettingsType.Image | SettingsType.ImageStr;

    /**
     * Define default image that can
     * be used if the image is not be set in data.
     */
    defaultImageSrc?: string;

    /**
     * Values:
     * fill - This is default. The replaced content is sized to fill the element's content box. If necessary, the object will be stretched or squished to fit
     * contain - The replaced content is scaled to maintain its aspect ratio while fitting within the element's content box
     * cover - The replaced content is sized to maintain its aspect ratio while filling the element's entire content box. The object will be clipped to fit
     * none - The replaced content is not resized
     */
    imageFitPolicy?: "contain" | "cover" | "fill" | "none" | "scale-down";
}
export interface ItemsMgmtDialogInfoSub<T, R> {
    /**
     * Tooltip for the column
     */
    tooltip?: string;

    /**
     * If the element is not unique show an error
     * Default false
     */
    isUnique?: boolean;

    /**
     * Check if the item is enabled for editing based on the row or the data
     * Default true
     */
    isEnabled?: (data: T, row: R, index: number) => boolean;

    /**
     * Create items from function
     */
    itemsFunc?: (data: T, row: R, index: number) => DropDownItem[];

    /**
     * Dynamic change of the type based on other settings in the row
     */
    typeFuncCell?: (data: T, row: R, index: number) => SettingsType;

    /**
     * Hide the element from the row
     */
    isHiddenCell?: (data: T, row: R, index: number) => boolean;

    /**
     * Extra styling in cell
     */
    styleCell?: (data: T, row: R, index: number) => React.CSSProperties;
}
export type ItemsMgmtDialogInfo<T, R> = ItemsMgmtDialogInfoSub<T, R> & SettingsElementAll<T>;

export interface SettingsElementItemsMgmtDialog<T, R = any> extends SettingsElement<T> {
    type: SettingsType.ItemsMgmtDialog | SettingsType.ItemsMgmtDialogStr;
    icon?: React.ReactNode;

    /**
     * Info for the items fields
     */
    itemsInfo: ItemsMgmtDialogInfo<T, R>[];

    /**
     * Get the new item. If undefined, no 'add' icon will be used.
     */
    createNewItem?: R | ((data: T, FilteringValue: any) => R);

    /**
     * Whether to support items add/delete.
     */
    readonly?: boolean;

    /**
     * Extra toggle in toolbar for boolean flags
     */
    toolbarToggle?: SettingsElementBoolean<T>[];

    /**
     * Static size for the body based on percentage [0-1]
     */
    dialogStaticSize?: number | { w: number, h: number };

    /**
     * Update data based on the new data after editing.
     */
    onSaveUpdate?: (newData: T) => T;

    /**
     * Check data if they are valid before save.
     */
    onCheckData?: (Data: T) => { valid: boolean, errorMsg?: string | React.ReactNode };


    /**
     * Support filtering mechanism
     */
    filteringList?: (Data: T) => { value: any, label: string }[];

    /**
     * Filtering show items
     */
    onFilteringRequest?: (Data: T, OriginalItems: R[], FilteringValue: any) => R[];
}
// ------------------------------------------------------------------------------------------------
export interface SettingsElementTabInfo<T> extends SettingsGroup<T> {
    id: string;
    icon: string;

}
export interface SettingsElementTabsGroup<T> extends Partial<SettingsElement<T>> {
    type: SettingsType.TabsGroup | SettingsType.TabsGroupStr;
    tabs: SettingsElementTabInfo<T>[];

    appearance: 'tabs' | 'line';
    tabPosition: 'top' | 'left' | 'right' | 'bottom';

    bodyStyle: ItemsStyle;
}
// ------------------------------------------------------------------------------------------------
export interface SettingsElementInfotip<T> extends SettingsElement<T> {
    type: SettingsType.InfoTip | SettingsType.InfoTipStr;

    /**
     * Info tip items
     */
    items: InfoTipItem[];
}
// ------------------------------------------------------------------------------------------------
export type SettingsElementAll<T> = SettingsElementItemsMgmtDialog<T, any> | SettingsElementNumber<T> |
    SettingsElementColorPicker<T> | SettingsElementString<T> | SettingsElementDropDown<T> | SettingsElementDialog<T> |
    SettingsElementImageFileInput<T> | SettingsElementSlider<T> | SettingsElementBoolean<T> |
    SettingsElementIcon<T> | SettingsElementExtendedIcon<T> | SettingsElementGeneric<T> | SettingsElementListItem<T> |
    SettingsElementGroup<T> | SettingsElementMarkdownString<T> | SettingsElementPalette<T> | SettingsElementHighlights<T> |
    SettingsElementDatetime<T> | SettingsElementTime<T> | SettingsElementBarcodeScanner<T> | SettingsElementButton<T> | SettingsElementImage<T> |
    SettingsElementDatetimeRange<T> | SettingsElementInfotip<T> | SettingsElementTextEditorString<T> | SettingsElementListOfItems<T> |
    SettingsElementCode<T> | SettingsElementAttachments<T> | SettingsElementRadioButtons<T> | SettingsElementButtonGroup<T> |
    SettingsElementTabsGroup<T>;
// ------------------------------------------------------------------------------------------------
export interface SettingsGroup<T = any> {
    name: string;

    /**
     * Settings elements of the specific group
     */
    elements?: SettingsElementAll<T>[];

    /**
     * Create elements based on the settings it self
     */
    elementsFunc?: (data: T) => SettingsElementAll<T>[];

    /*
     * If true, then group start open,
     * Default: false
    */
    startOpen?: boolean;

    /**
     * Style the group header
     */
    headerStyle?: ItemsStyle;

    /**
     * Left icon
     */
    headerLeftIcon?: string;
}
// --------------------------------------------------